import { useT } from '@hana/i18n';
import { Chip, Table, type Column } from '@hana/ui';
import { MS, fi, pc } from './data';
import { COLS, KPI, SVM, mser, people, type Field, type Person, type Role } from './roleData';
import { BarsPanel, Card, Kpis, type BarRow, type Kpi } from './panels';
import { Donut } from './SvcRole';
import './i18nPerson';
import b from './bod.module.css';
import r from './svcRole.module.css';

// Mint-BOD · Hiệu suất dịch vụ › Bộ phận › xem từng nhân sự (Mint-BOD-Person, prop `ppl`): ô số, việc theo tháng so TB mảng,
// cơ cấu dịch vụ, so với TB mảng, khách phụ trách, Nhận định, việc đang mở. Chưa gồm khối tiền của Sale (cần quyền money).
const CW = 720, CH = 180, SW = 60;
const LOW_GOOD: readonly Field[] = ['amend', 'resp', 'conf', 'miss', 'roll', 'inc', 'wait', 'op', 'stl', 'emp', 'ret', 'od'];
const CUS = ['RUITONGVN-SC', 'GREENBUD', 'QUEKYCORP', 'NAMMY', 'PHUCUONGEXIM', 'TAMNGOC', 'COCOGROUP', 'ARRIVE', 'MINHPHAT'];
const SHARE = [0.31, 0.22, 0.16, 0.11, 0.07];
const DUE = ['05/Nov', '05/Nov', '06/Nov', '06/Nov', '07/Nov', '10/Nov'];
const ST = [['over', 'err'], ['cus', 'warn'], ['doing', 'info'], ['doing', 'info'], ['line', 'warn'], ['doing', 'info']] as const;
const c1 = (v: number) => String(Math.round(v * 10) / 10).replace('.', ',');
const sg = (v: number) => (v >= 0 ? '+' : '−') + Math.abs(v).toFixed(1).replace('.', ',') + '%';

export function SvcPerson({ role, name }: { role: Role; name: string }) {
  const t = useT();
  const R = people(role);
  const pj = Math.max(0, R.findIndex((x) => x.nm === name));
  const x = R[pj]!;
  const tot2 = R.reduce((a, y) => a + y.n, 0) || 1;
  const ser = mser(x.n, pj);
  const tot = ser.reduce((a, v) => a + v, 0);
  const avgS = MS.map((_, i) => Math.round(R.reduce((a, y, j) => a + mser(y.n, j)[i]!, 0) / R.length));
  const mx = Math.max(...ser, ...avgS) * 1.15;
  const K = KPI[role], kv = x[K.f], kAvg = R.reduce((a, y) => a + y[K.f], 0) / R.length;
  const kfmt = (v: number) => (K.unit === 'pct' ? `${v}%` : t('bod.role.times', { n: v }));
  const kbad = (K.lo != null && kv < K.lo) || (K.hi != null && kv > K.hi);
  const r3 = ser[9]! + ser[10]! + ser[11]!, p3 = ser[6]! + ser[7]! + ser[8]!;
  const dq = ((r3 - p3) / (p3 || 1)) * 100;
  const tiles: Kpi[] = [
    { k: t(`bod.role.lb.${role}`), v: fi(x.n), sub: t('bod.person.t1', { p: pc((x.n / tot2) * 100) }) },
    { k: t(`bod.role.kp.${role}`), v: kfmt(kv), vtone: kbad ? 'warn' : 'ok', sub: t('bod.person.avg', { v: c1(Math.round(kAvg * 10) / 10) }) },
    { k: t('bod.person.q'), v: fi(r3), sub: t('bod.person.qv', { d: sg(dq) }), tone: r3 >= p3 ? 'ok' : 'err' },
    { k: t('bod.role.m.open'), v: fi(x.op) },
    { k: t('bod.role.c.late'), v: fi(x.late), vtone: x.late ? 'warn' : undefined },
  ];

  const met: { label: string; f: Field | 'late' }[] = [...COLS[role].map((m) => ({ label: t(`bod.role.m.${m.id}`), f: m.f as Field | 'late' })), { label: t('bod.role.c.late'), f: 'late' }];
  const cmp = met.map(({ label, f }) => {
    const v = x[f], av = R.reduce((a, y) => a + y[f], 0) / R.length;
    const ix = av ? Math.round((v / av) * 100) : 100;
    const good = f !== 'late' && !LOW_GOOD.includes(f) ? ix >= 100 : ix <= 100;
    return { label, ix, good, row: { k: label, n: ix, tone: good ? 'ok' : 'warn', s: t('bod.person.deptAvg', { v: c1(Math.round(av * 10) / 10) }), v: `${c1(v)} · ${ix}` } satisfies BarRow };
  });
  const cu = [0, 1, 2, 3, 4].map((q) => CUS[(pj * 2 + role.length + q) % CUS.length]!).map((c, q) => ({ c, n: Math.round(x.n * SHARE[q]!) }));
  const sv = SVM[role];
  const names = (c: string) => (c.startsWith('acc.') ? t(`bod.role.${c}`) : c);
  const weak = cmp.filter((c) => !c.good).map((c) => c.label.toLowerCase());
  const ins = [
    t('bod.person.i1', { a: x.nm, p: pc((x.n / tot2) * 100), d: t(r3 >= p3 ? 'bod.person.up' : 'bod.person.dn'), q: sg(dq).slice(1) }),
    weak.length ? t('bod.person.i2', { l: weak.join(', ') }) : t('bod.person.i2ok'),
    t('bod.person.i3', { c: cu[0]!.c, p: pc((cu[0]!.n / (x.n || 1)) * 100), a: x.nm }),
  ];
  const jobs = [0, 1, 2, 3, 4, 5].map((q) => ({ id: `SE26${1040100 + pj * 97 + q * 41}`, cus: cu[q % cu.length]!.c, svc: names(sv[q % sv.length]![0]), due: DUE[q]!, st: ST[(q + (x.late ? 0 : 1)) % ST.length]! }));
  type J = (typeof jobs)[number];
  const jcols: Column<J>[] = [
    { key: 'id', header: 'JobID', width: '16%', cell: (j) => <a href={`/ezy/jobs/${j.id}`}><b>{j.id}</b></a> },
    { key: 'cus', header: t('bod.person.c.cus'), width: '22%', cell: (r) => r.cus },
    { key: 'svc', header: t('bod.person.c.svc'), width: '12%', cell: (r) => <Chip tone="info">{r.svc}</Chip> },
    { key: 'due', header: t('bod.person.c.due'), width: '14%', cell: (r) => <span style={{ color: r.st[1] === 'err' ? 'var(--err)' : 'var(--tx2)' }}>{r.due}</span> },
    { key: 'st', header: t('bod.person.c.st'), width: '16%', cell: (r) => <Chip tone={r.st[1]}>{t(`bod.person.st.${r.st[0]}`)}</Chip> },
  ];

  const y = (v: number) => (CH - (Math.max(0, v) / mx) * (CH - 12)).toFixed(1);
  const bars = ser.map((v, i) => { const h = (Math.max(0, v) / mx) * (CH - 12); return h > 0.2 ? `M${(i * SW + 12).toFixed(1)} ${y(v)}h36v${h.toFixed(1)}h-36z ` : ''; }).join('');
  const line = avgS.map((v, i) => `${i ? 'L' : 'M'}${(i * SW + SW / 2).toFixed(1)} ${y(v)}`).join(' ');
  return (
    <>
      <Kpis items={tiles} />
      <Card title={t('bod.person.k1', { n: x.nm })} sub={t('bod.person.s1', { n: x.nm })} span={8}>
        <div className={b.leg}><span><i className={r.p0} />{x.nm}</span><span><i className={`${b.ln} ${b.cWarn}`} />{t('bod.person.avgS')}</span><em>{t('bod.svct.peak', { n: fi(Math.max(...ser)) })}</em></div>
        <svg role="img" aria-label={`${x.nm}: ${MS.map((m, i) => `${m} ${ser[i]}`).join(', ')}`} width="100%" height="180" viewBox={`0 0 ${CW} ${CH}`} preserveAspectRatio="none">
          <path d={[0.25, 0.5, 0.75, 1].map((f) => `M0 ${(CH - f * (CH - 12)).toFixed(1)}H${CW}`).join(' ') + ` M0 ${CH - 0.5}H${CW}`} className={b.gridL} vectorEffect="non-scaling-stroke" />
          <path d={bars} className={r.bar0} />
          <path d={line} className={r.avgL} vectorEffect="non-scaling-stroke" />
        </svg>
        <div className={b.xl} aria-hidden="true">{MS.map((k) => <span key={k}>{k}</span>)}</div>
      </Card>
      <Card title={t(role === 'ACC' ? 'bod.person.k2acc' : 'bod.person.k2')} sub={t('bod.person.s2')} span={4}>
        <Donut rows={sv.map(([c, w]) => [names(c), Math.round(tot * w)] as const)} tot={tot} unit={t('bod.role.work')} />
      </Card>
      <BarsPanel title={t('bod.person.k3')} sub={t('bod.person.s3')} span={6} rows={cmp.map((c) => c.row)} vw="72px" bold />
      <BarsPanel title={t('bod.person.k4')} sub={t('bod.person.s4')} span={6} rows={cu.map((c) => ({ k: c.c, n: c.n, v: fi(c.n) }))} vw="48px" bold />
      <Card title={t('bod.svc.ins')} sub={x.nm} span={12}><ul className={b.ins}>{ins.map((i) => <li key={i}>{i}</li>)}</ul></Card>
      <Card title={t('bod.person.k5', { n: x.nm })} sub={t('bod.person.s5')} span={12}>
        <Table label={t('bod.person.k5', { n: x.nm })} columns={jcols} rows={jobs} rowKey={(r) => r.id} dense />
      </Card>
    </>
  );
}
export type { Person };
